import { type ReactNode } from 'react';
import { KeyHint } from './KeyHint';
import { ServiceStatusTray, type ServiceEntry } from './ServiceStatusTray';

export type AppHeaderNavItem = {
  key: string;
  label: string;
  href: string;
};

export type AppHeaderProps = {
  activeKey?: string;
  navItems: AppHeaderNavItem[];
  services?: ServiceEntry[];
  user?: { name: string; avatarUrl?: string };
  onSearchClick?: () => void;
  // Slot for the project's router-aware Link component. Defaults to an
  // anchor tag — fine for Storybook and any consumer that doesn't need
  // client routing.
  renderLink?: (props: {
    href: string;
    children: ReactNode;
    active: boolean;
    className: string;
  }) => ReactNode;
  // Optional service-degraded banner reveal under the header.
  degradedBanner?: ReactNode;
  // Extra controls rendered in the right cluster after the user avatar
  // (e.g., theme toggle, language picker). Defaults to none.
  rightExtras?: ReactNode;
};

function defaultRender({
  href,
  children,
  className,
}: {
  href: string;
  children: ReactNode;
  className: string;
}) {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

/**
 * Composed AppHeader: logo + nav + search trigger + ServiceStatusTray +
 * optional user avatar slot. Router-agnostic — supply `renderLink` to
 * wire it to TanStack Router (or any other) for active-link styling.
 */
export function AppHeader({
  activeKey,
  navItems,
  services,
  user,
  onSearchClick,
  renderLink = defaultRender,
  degradedBanner,
  rightExtras,
}: AppHeaderProps) {
  const anyDegraded = services?.some((s) => s.status !== 'healthy') ?? false;
  return (
    <header role="banner" className="sticky top-0 z-10 bg-gray-50">
      <div className="flex items-center gap-sp-6 border-b border-gray-300 px-sp-6 py-sp-3">
        <a href="/" aria-label="PDM home" className="flex items-center gap-sp-2">
          <LogoGlyph />
          <span className="text-fs-body font-medium text-gray-900">PDM</span>
        </a>

        <nav aria-label="Primary" className="flex items-center gap-sp-4">
          {navItems.map((item) =>
            renderLink({
              href: item.href,
              active: item.key === activeKey,
              className:
                'text-fs-meta transition-colors ' +
                (item.key === activeKey
                  ? 'font-medium text-gray-900'
                  : 'text-gray-700 hover:text-gray-900'),
              children: item.label,
            }),
          )}
        </nav>

        <div className="ml-auto flex items-center gap-sp-3">
          <button
            type="button"
            onClick={onSearchClick}
            className="inline-flex items-center gap-sp-2 rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-1 text-fs-meta text-gray-700 hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
          >
            <span>Search</span>
            <KeyHint keys={['/']} />
          </button>

          {services ? <ServiceStatusTray services={services} /> : null}

          {user ? (
            <span
              aria-label={`Signed in as ${user.name}`}
              className="inline-flex h-7 w-7 items-center justify-center rounded-r-chip bg-gray-900 text-fs-meta font-medium text-gray-50"
            >
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="h-full w-full rounded-r-chip object-cover" />
              ) : (
                initials(user.name)
              )}
            </span>
          ) : null}

          {rightExtras}
        </div>
      </div>
      {anyDegraded && degradedBanner ? (
        <div className="border-b border-warn bg-warn-bg px-sp-6 py-sp-2 text-fs-meta text-gray-900">
          {degradedBanner}
        </div>
      ) : null}
    </header>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
}

function LogoGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden focusable="false">
      <rect x="2" y="2" width="16" height="16" rx="3" className="fill-gray-900" />
      <circle cx="14" cy="6" r="2" className="fill-info" />
    </svg>
  );
}
